import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { Link } from "@mui/material";

import { format } from "date-fns";

import { FunctionComponent } from "react";

import { Icon } from "@/components";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { classNames } from "@/utils/classNames";
import { collapsedSignalText } from "@/utils/signals/collapsedSignalText";

import { TrackerSignalResult } from "../../ProfileTracker/schema/trackerSignalResult";
import {
	getSignalCategoryColor,
	getSignalPriorityColor,
	getSignalPriorityLabel,
} from "../../ProfileTracker/components/CustomColumns/SignalTimeline/signalTimelineUtils";
import { LinkedinSignalPostPreview } from "../../ProfileTracker/components/CustomColumns/SignalTimeline/LinkedinSignalPostPreview";

interface TrackerSignalCellProps {
	signal?: TrackerSignalResult | null;
}

/**
 * The "Signal" column of the List table: the latest tracker signal, its source link and a tooltip
 * with title, post, reasoning and summary. PREC-5037/5038 makes the collapsed text say what the
 * signal is about (its summary) rather than only who posted it.
 */
export const TrackerSignalCell: FunctionComponent<TrackerSignalCellProps> = ({ signal }) => {
	if (!signal) {
		return <div className="text-gray-400 text-sm italic">No signal</div>;
	}

	const signalUrl = signal.linkedinPost?.share_url || signal.sourceUrl;
	const collapsed = collapsedSignalText({ title: signal.title, summary: signal.summary });

	const tooltipContent = (
		<div className="flex flex-col gap-2 max-w-[350px] p-1">
			{/* Badges row */}
			<div className="flex items-center gap-2 flex-wrap">
				{signal.priority && (
					<span
						className={classNames(
							"px-2 py-0.5 text-xs font-semibold rounded border",
							getSignalPriorityColor(signal.priority)
						)}
					>
						{getSignalPriorityLabel(signal.priority)}
					</span>
				)}
				{signal.signalCategory && (
					<span
						className={classNames(
							"px-2 py-0.5 text-xs font-semibold rounded border",
							getSignalCategoryColor(signal.signalCategory)
						)}
					>
						{signal.signalCategory}
					</span>
				)}
			</div>

			{/* Title */}
			{signal.title && (
				<h4 className="text-sm font-semibold text-text leading-5">{signal.title}</h4>
			)}

			{signal.linkedinPost && <LinkedinSignalPostPreview signal={signal} />}

			{/* Reasoning */}
			{signal.reasoning && (
				<div className="p-2 bg-blue-50 border-l-2 border-blue-400 rounded">
					<h5 className="text-xs font-semibold text-gray-700 mb-1">Why is this relevant</h5>
					<p className="text-xs text-gray-600">{signal.reasoning}</p>
				</div>
			)}

			{/* Summary */}
			{signal.summary && <p className="text-sm text-gray-600">{signal.summary}</p>}

			{/* Timestamps */}
			<div className="flex flex-col gap-1 text-xs text-gray-500 border-t pt-2 mt-1">
				{signal.publishedDate && (
					<span>Published: {format(new Date(signal.publishedDate), "d MMM yyyy, HH:mm")}</span>
				)}
				<span>Collected: {format(new Date(signal.createdAt), "d MMM yyyy, HH:mm")}</span>
			</div>
		</div>
	);

	return (
		<div className="flex items-center gap-2 w-[200px] max-w-[200px]">
			{signalUrl && (
				<Link
					className="flex-shrink-0"
					href={signalUrl}
					target="_blank"
					onClick={(e) => e.stopPropagation()}
				>
					{signalUrl.includes("linkedin.com") ? (
						<LinkedinIcon />
					) : (
						<Icon className="w-4 h-4 text-gray-500 hover:text-blue-action" icon={faExternalLink} />
					)}
				</Link>
			)}
			<Tooltip content={tooltipContent}>
				<span className="text-sm overflow-hidden text-ellipsis whitespace-nowrap block max-w-[170px] cursor-help">
					{collapsed.text ?? <span className="text-gray-400 italic">No title</span>}
				</span>
			</Tooltip>
		</div>
	);
};
